import React, { useEffect, useState } from 'react'
import { Modal as BootstrapModal, Tab } from 'react-bootstrap'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import { useGetSupportedObjectsQuery } from '../../../store/integrations/integrations'
import { Button } from '../../../components/Button/Button'

import { Tabs } from '../../../components/Tabs/Tabs'
import { CrmMappingModalFooter, CrmMappingModalHeader } from './utils'
import { AIGeneratedFieldsTab, AIGeneratedSingleFieldTab } from './AIGeneratedFields'
import { ContactLinkTab } from './ContactLinkTab'
import { FormProvider, useForm } from 'react-hook-form'
import { MissingRequiredFields } from './MissingRequiredFields'
import { MeetingDataTab } from './MeetingDataTab'
import { AIFormValue, CrmIntegrationOperationType } from './EditAIFormView'
import { CRMFieldMappings, DescriptorType } from '../../../models/AiFeature'
import { generateId } from '../../../common/util'

const INTEGRATION_LABELS: Record<string, string> = {
  wealthbox: 'Wealthbox',
  redtail: 'Redtail',
  salesforce: 'Salesforce',
  xlr8: 'XLR8',
  practifi: 'Practifi',
}

type CrmMappingMultiFieldModalProps = {
  showCrmModal: boolean
  onSave: (crmFieldMappings: CRMFieldMappings) => void
  onCancel: () => void
  connector: string
  integrationFormFields?: CRMFieldMappings | null
  formFields: AIFormValue[]
  operationType?: CrmIntegrationOperationType
}

export type FormRow = {
  key: string
  objectName: string | null
  fieldName: string | null
  value: string | null
  type: DescriptorType
}

export type FormValues = {
  mappingFormFields: FormRow[]
}

export const CrmMappingMultiFieldModal: React.FC<CrmMappingMultiFieldModalProps> = ({
  showCrmModal,
  onSave,
  onCancel,
  connector,
  integrationFormFields,
  formFields,
  operationType,
}) => {
  const defaultValues = {
    mappingFormFields: [],
  }
  const form = useForm<FormValues>({ defaultValues })
  const [activeTab, setActiveTab] = useState('AI_GENERATED_FIELDS')

  const accountId = useAppSelector(selectAccountId)

  useEffect(() => {
    if (!integrationFormFields) {
      return
    }

    const newFormFields: FormRow[] = []
    if (operationType === 'merged_fields') {
      const objectName = Object.keys(integrationFormFields)[0]
      const fieldName = Object.keys(integrationFormFields[objectName])[0]
      newFormFields.push({ key: generateId(), objectName, fieldName, value: 'all', type: 'field' })
      Object.entries(integrationFormFields).forEach(([objectName, objectFields]: [any, any]) => {
        Object.entries(objectFields).forEach(([fieldName, mappings]: [any, any]) => {
          mappings.forEach((mapping: any) => {
            if (mapping.type != 'static') {
              newFormFields.push({ key: generateId(), objectName, fieldName, value: mapping.value, type: mapping.type })
            }
          })
        })
      })
    } else if (operationType === 'discrete_fields') {
      Object.entries(integrationFormFields).forEach(([objectName, objectFields]: [any, any]) => {
        Object.entries(objectFields).forEach(([fieldName, mappings]: [any, any]) => {
          mappings.forEach((mapping: any) => {
            if (mapping.type != 'static') {
              newFormFields.push({ key: generateId(), objectName, fieldName, value: mapping.value, type: mapping.type })
            }
          })
        })
      })
    }

    form.setValue('mappingFormFields', newFormFields)
  }, [integrationFormFields])

  const { data: supportedObjects, isFetching: isSupportedObjectsLoading } = useGetSupportedObjectsQuery(
    {
      accountId,
      connector: connector,
    },
    { refetchOnMountOrArgChange: true },
  )

  const handleCancel = () => {
    form.reset()
    onCancel()
  }

  const onSubmit = (formValues: FormValues) => {
    const result = formValues.mappingFormFields.reduce((acc: CRMFieldMappings, row) => {
      const key = row.objectName
      if (!key || !row.fieldName || !row.value) {
        return acc
      }
      if (!acc[key]) {
        acc[key] = {}
      }
      if (!acc[key][row.fieldName]) {
        acc[key][row.fieldName] = []
      }
      acc[key][row.fieldName].push({ value: row.value, type: row.type })
      return acc
    }, {})

    onSave(result)
  }

  useEffect(() => {
    if (operationType === 'discrete_fields') {
      setActiveTab('AI_GENERATED_FIELDS')
    } else if (operationType === 'merged_fields') {
      setActiveTab('AI_GENERATED_SINGLE_FIELD')
    }
  }, [operationType])

  return (
    <FormProvider {...form}>
      <BootstrapModal className='modal-lg' show={showCrmModal} onHide={handleCancel} centered>
        <CrmMappingModalHeader selectedIntegration={connector} integrationLabels={INTEGRATION_LABELS} />

        <BootstrapModal.Body>
          <MissingRequiredFields connector={connector} />

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => {
              if (k !== null) {
                setActiveTab(k)
              }
            }}
            id='crm-mapping-tabs'
            className='mb-3'
            mountOnEnter
            unmountOnExit
          >
            {operationType === 'discrete_fields' ? (
              <Tab eventKey='AI_GENERATED_FIELDS' title='AI-GENERATED FIELDS'>
                <AIGeneratedFieldsTab formFields={formFields} connector={connector} />
              </Tab>
            ) : operationType === 'merged_fields' ? (
              <Tab eventKey='AI_GENERATED_SINGLE_FIELD' title='AI-GENERATED SINGLE FIELD'>
                <AIGeneratedSingleFieldTab formFields={formFields} connector={connector} />
              </Tab>
            ) : null}
            <Tab eventKey='MEETING_DATA' title='MEETING DATA'>
              <MeetingDataTab connector={connector} />
            </Tab>

            <Tab eventKey='CONTACT_LINKING' title='CONTACT LINKING'>
              <ContactLinkTab connector={connector} />
            </Tab>
          </Tabs>
        </BootstrapModal.Body>

        <CrmMappingModalFooter onCancel={handleCancel} onApply={form.handleSubmit(onSubmit)} />
      </BootstrapModal>
    </FormProvider>
  )
}

interface UnmappedFieldsModalProps {
  show: boolean
  onHide: () => void
  crmIntegrationOption?: CrmIntegrationOperationType | null
  unmappedFieldCount: number
  onCompleteMapping: () => void
  onSaveForm: () => void
}

export const UnmappedFieldsModal: React.FC<UnmappedFieldsModalProps> = ({
  show,
  onHide,
  crmIntegrationOption,
  unmappedFieldCount,
  onCompleteMapping,
  onSaveForm,
}) => {
  return (
    <BootstrapModal show={show} onHide={onHide} centered>
      <BootstrapModal.Header closeButton>
        <BootstrapModal.Title>Unmapped Form Fields to CRM</BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body>
        {crmIntegrationOption === 'discrete_fields' ? (
          <>
            {unmappedFieldCount} form field
            {unmappedFieldCount !== 1 ? 's are' : ' is'}n't mapped to your CRM. AI will fill these during meetings, but the data won't be saved to your CRM.
          </>
        ) : crmIntegrationOption === 'merged_fields' ? (
          <>Form data won't sync to your CRM because no destination field is selected. Choose where to save the combined form responses.</>
        ) : null}
      </BootstrapModal.Body>
      <BootstrapModal.Footer>
        <Button variant='secondary' onClick={onCompleteMapping}>
          Complete Mapping
        </Button>
        <Button variant='primary' onClick={onSaveForm}>
          Save Form
        </Button>
      </BootstrapModal.Footer>
    </BootstrapModal>
  )
}
